import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { Link as ScrollLink } from 'react-scroll';
import { useInView } from 'react-intersection-observer';
import ReviewStars from "../components/ReviewStars";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import miniCityscape from "../images/vesta/mini-cityscape.svg";
import image1 from "../images/vesta/Conveyancing.png";
import image2 from "../images/vesta/StampDuty.svg";
import image3 from "../images/vesta/large-cityscape-crop.png";
import image4 from "../images/vesta/large-cityscape-removebg.png";
import { Link, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import PropertyServicesCarousel from '../pages/components/PropertyServicesCarousel';

function WhiteLabel() {
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(-150);
  
  // Improved InView configuration with thresholds and rootMargin for smoother animations
  const { ref: sectionRef1, inView: inView1 } = useInView({ 
    triggerOnce: true, 
    threshold: 0.2,
    rootMargin: '0px 0px -100px 0px'
  });
  const { ref: sectionRef2, inView: inView2 } = useInView({ 
    triggerOnce: true, 
    threshold: 0.2,
    rootMargin: '0px 0px -100px 0px'
  });
  const { ref: sectionRef3, inView: inView3 } = useInView({ 
    triggerOnce: true, 
    threshold: 0.2,
    rootMargin: '0px 0px -100px 0px'
  });
  const { ref: sectionRef4, inView: inView4 } = useInView({ 
    triggerOnce: true, 
    threshold: 0.2,
    rootMargin: '0px 0px -100px 0px'
  });
  
  // For buttons, use triggerOnce to prevent repeated animations
  const { ref: buttonRef1, inView: buttonInView1 } = useInView({ triggerOnce: true });
  const { ref: buttonRef2, inView: buttonInView2 } = useInView({ triggerOnce: true });
  const { ref: buttonRef3, inView: buttonInView3 } = useInView({ triggerOnce: true });
  
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  useEffect(() => {
    const MOBILE_OFFSET = -340;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      setScrollOffset(newWidth <= 768 ? MOBILE_OFFSET : -30);
    };

    // Throttle the resize event for better performance
    let resizeTimer: string | number | NodeJS.Timeout | null | undefined;
    const throttledResize = () => {
      if (!resizeTimer) {
        resizeTimer = setTimeout(() => {
          resizeTimer = null;
          handleResize();
        }, 100);
      }
    };

    window.addEventListener('resize', throttledResize);
    handleResize(); // Call once to set initial values

    return () => {
      window.removeEventListener('resize', throttledResize);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, []);

  //Autoplay Video
  const handlePlayVideo = () => {
    setIsPlaying(true);
  };

  // Button Navigation
  const handleSignUp = () => {
    navigate('/register');
  };

  const handleGetStarted = () => {
    navigate('/listing');
  };

  const handleLearnMore = () => {
    navigate('/services');
  };

  const handleHover = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, hoverColor: string) => {
    // Toggle between normal and hover colors
    const hoverBg = hoverColor || themes.color1;
    const originalBg = themes.color2;
    
    // If mouse over, switch to hover color
    if (e.type === 'mouseover') {
      e.currentTarget.style.background = hoverBg;
      // Set appropriate text color based on background
      e.currentTarget.style.color = isWhiteOrShade(hoverBg) ? "#000000" : "#ffffff";
      // Add border if the background is white or light shade
      if (isWhiteOrShade(hoverBg)) {
        e.currentTarget.style.border = "1px solid #000000";
      } else {
        e.currentTarget.style.border = "none";
      }
    } 
    // If mouse out, switch back to original color
    else {
      e.currentTarget.style.background = originalBg;
      // Set appropriate text color based on background
      e.currentTarget.style.color = isWhiteOrShade(originalBg) ? "#000000" : "#ffffff";
      // Reset border based on original background
      if (isWhiteOrShade(originalBg)) {
        e.currentTarget.style.border = "1px solid #000000";
      } else {
        e.currentTarget.style.border = "none";
      }
    }
  };

  // White
   // White
   const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
  
    // 1) explicit white checks
    if (
      s === "white" ||
      s === "#fff" ||
      s === "#ffffff" ||
      s === "rgb(255,255,255)" ||
      s === "rgba(255,255,255,1)"
    ) {
      return true;
    }
  
    let r: number, g: number, b: number;
  
    // 2) hex formats
    if (s.startsWith("#")) {
      let hex = s.slice(1);
      if (hex.length === 3) {
        // expand `#abc` → `#aabbcc`
        hex = hex.split("").map(ch => ch + ch).join("");
      }
      if (hex.length !== 6 || !/^[0-9a-f]{6}$/i.test(hex)) {
        return false;
      }
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
    // 3) rgb/rgba formats
    else {
      const m = s.match(
        /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
      );
      if (!m) return false;
      r = Number(m[1]);
      g = Number(m[2]);
      b = Number(m[3]);
    }
  
    // 4) threshold for "almost white"
    return r >= 235 && g >= 235 && b >= 235;
  };
  


  return (
    <div className="Home bg-primarycolor pt-28" style={{ background: getThemes && getThemes.color1 }}>
      <Helmet>
        <title>
          {`${getThemes?.name || 'Property Investment'} | Professional Property Investment Services`}
        </title>

        <meta 
          name="description" 
          content={`${getThemes?.name} offers professional property investment and portfolio building services. Find high-yielding buy-to-let properties sourced and vetted by ${getThemes?.name}. Expert property sourcing and management solutions.`}
        />

        <meta 
          name="keywords" 
          content={`${getThemes?.name}, property investment, buy-to-let properties, property portfolio, ${getThemes?.website?.replace('https://', '').replace('www.', '')}, property sourcing, UK property investment`}
        />

        <meta 
          property="og:title" 
          content={`${getThemes?.name} | Build Your Property Investment Portfolio`}
        />
        <meta 
          property="og:description" 
          content={`Partner with ${getThemes?.name} to discover premium property investment opportunities. Expert property sourcing and management services tailored to your investment goals.`}
        />
        <meta property="og:type" content="website" />
        {getThemes?.logo && <meta property="og:image" content={getThemes.logo} />}
        
        <link rel="canonical" href={getThemes?.website ? `${getThemes.website}${window.location.pathname}` : window.location.href} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": getThemes?.name,
            "description": `${getThemes?.name} provides professional property investment and portfolio building services in the UK.`,
            "url": getThemes?.website || window.location.origin,
            "logo": getThemes?.logo,
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "GB"
            }
          })}
        </script>
      </Helmet>

      <style>
        {`
          @keyframes slide-in-right {
            from {
              opacity: 0;
              transform: translateX(50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes slide-in-left {
            from {
              opacity: 0;
              transform: translateX(-50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes pulse {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
            }
          }

          @keyframes pulse-play {
              0% {
                transform: scale(1);
                box-shadow: 0 0 0 0 ${isWhiteOrShade(themes.color2) ? "rgba(0, 0, 0, 0.7)" : themes.color2};
              }
              
              70% {
                transform: scale(1.1);
                box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
              }
              
              100% {
                transform: scale(1);
                box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
              }
            }

            .video-play-button {
              animation: pulse-play 2s infinite;
              transition: all 0.3s ease;
            }
              

            .video-play-button:hover {
              animation: none;
              transform: scale(1.2);
              background-color: ${isWhiteOrShade(themes.color2) ? "rgba(0, 0, 0, 0.3)" : themes.color2};
            }

            .video-title {
              text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              transition: transform 0.3s ease;
            }

            .video-overlay:hover .video-title {
              transform: translateY(-5px);
            }

          .slide-in-right {
            animation: slide-in-right 0.6s ease-out forwards;
            will-change: transform, opacity;
          }

          .slide-in-left {
            animation: slide-in-left 0.6s ease-out forwards;
            will-change: transform, opacity;
          }

          .pulse {
            animation: pulse 1.5s ease-in-out;
          }

          .banner-text-container {
            text-align: left;
          }

          .banner-buttons-container {
            display: flex;
            gap: 1rem;
          }

          /* Pre-load state for animation elements to prevent layout shifts */
          .pre-animation-right {
            opacity: 0;
            transform: translateX(50px);
          }
          
          .pre-animation-left {
            opacity: 0;
            transform: translateX(-50px);
          }

          /* Media Query for Mobile Devices */
          @media (max-width: 768px) {
            .banner-text-container {
              text-align: center;
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 0 20px;
            }

            .banner-text-container h3,
            .banner-text-container p {
              text-align: center;
            }

            .banner-buttons-container {
              flex-direction: column;
              width: 100%;
            }

            .banner-button {
              width: 100%;
            }
          }
          @media (min-width: 1024px) {
            .search-properties-button {
              max-width: 265px !important; /* Increased from 285px to 300px for more noticeable width */
              min-width: 265px !important; /* Added min-width to ensure consistent sizing */
            }
          }
        `}
      </style>

      {/* Banner */}
      <div className="relative w-full" style={{ 
        backgroundImage: `url(${backgroundPattern})`, 
        backgroundRepeat: 'repeat', 
        backgroundSize: '100px 100px' 
      }}>
        <div className="absolute bottom-0 right-0 lg:w-2/5 w-full lg:h-auto h-auto xl:w-1/3 2xl:w-3/5" style={{ height: 'auto', minHeight: '100px' }}>
          <img
            src={miniCityscape}
            className="w-full h-auto"
            alt="Mini Cityscape"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </div>
        <div className="max-w-7xl m-auto grid lg:grid-cols-2 grid-cols-1 lg:pt-16 pt-5 pb-20 gap-x-4 justify-center items-center relative">
          <div className="p-5 relative z-10">
            <h3 className="lg:text-5xl text-2xl font-poppins lg:font-semibold font-medium text-center lg:text-left lg:pb-7 pb-3 lg:leading-snug lg:pr-12 lg:tracking-wide tracking-wider"
              style={{ 
                color: isWhiteOrShade(getThemes?.color1 || "") ? "#000000" : (getThemes?.txtColor || "#ffffff") 
              }}>
              Build a high-yielding residential property portfolio with ease
            </h3>
            <p className="text-base font-poppins font-normal text-center lg:text-left leading-7 tracking-wide lg:pb-3 pb-3 px-4 lg:px-0"
              style={{ 
                color: isWhiteOrShade(getThemes?.color1 || "") ? "#000000" : (getThemes?.txtColor || "#ffffff") 
              }}>
              We use innovative, proprietary technology to help ambitious
              investors build, grow and manage high-yielding, sustainable
              property portfolios. Our hassle-free investment experience allows
              you to sit back and watch your wealth grow.
            </p>
            <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-4 pt-5">
            <Link to="/listing" className="w-full lg:w-auto max-w-[280px] search-properties-button">
  <div
    className="py-2.5 w-full rounded-full text-center transition-all duration-300 hover:scale-105"
    style={{
      background: getThemes?.color2 ?? "#0055cc",
      color: isWhiteOrShade(getThemes?.color2 || "") ? "#000000" : "#ffffff",
      border: `1px solid ${isWhiteOrShade(getThemes?.color1 || "") ? "#000000" : "#ffffff"}`,
    }}
    onMouseOver={e => {/* ... */}}
    onMouseOut={e => {/* ... */}}
  >
    Search Properties
  </div>
</Link>
              <ScrollLink 
                to="section1" 
                smooth={true} 
                duration={700} 
                offset={scrollOffset}
                className="cursor-pointer banner-button w-full lg:w-auto max-w-[280px]" 
                style={{color: themes.txtColor}}
              >
                <div className="flex gap-x-3 px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105 justify-center">
                  Learn More
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={themes.txtColor} 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="feather feather-arrow-right"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </ScrollLink>
            </div>
          </div>
          <div className="relative lg:block block">
           <div className="relative rounded-xl overflow-hidden mt-4 bg-white" style={{ 
             boxShadow: '0 35px 60px -15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 0, 0, 0.15), 0 0 40px rgba(0, 0, 0, 0.15)',
             transform: 'translateY(-8px)'
           }}>
              <div className="relative" style={{ height: '360px' }}>
                {/* Video player container */}
                <div className="absolute inset-0">
                  <ReactPlayer
                    url="https://whitelabel-uploads.s3.amazonaws.com/Explainer+Video.mp4"
                    controls={isPlaying}
                    playing={isPlaying}
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0, display: isPlaying ? 'block' : 'none' }}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload',
                        },
                      },
                    }}
                    onPause={() => setIsPlaying(false)}
                    onContextMenu={(e: { preventDefault: () => any; }) => e.preventDefault()}
                  />
                </div>
                
                
                {/* Custom overlay with How It Works and play button - with enforced white background */}
                  {!isPlaying && (
                    <div 
                      className="absolute inset-0 z-30 flex flex-col items-center justify-center cursor-pointer"
                      onClick={handlePlayVideo}
                      style={{ backgroundColor: "white" }}
                    >
                      <h2 className="text-black text-3xl font-semibold mb-6 video-title">How It Works</h2>
                      <div className="video-play-button w-20 h-20 rounded-full bg-black flex items-center justify-center relative z-10">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="32" 
                          height="32" 
                          viewBox="0 0 24 24" 
                          fill="currentColor" 
                          className="text-white ml-1"
                        >
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <<============Video============>> */}

      <div className="w-full m-auto bg-white-f2f9fa">
        <div className="max-w-7xl m-auto py-16">
          <div className="m-auto">
          <PropertyServicesCarousel />
          </div>
        </div>

        <div className="relative w-full" style={{ 
          backgroundImage: `url(${backgroundPattern})`, 
          backgroundRepeat: 'repeat', 
          backgroundSize: '75px 75px' 
        }}>
          <div className="max-w-7xl m-auto py-16 px-4 md:px-8">
            <div className="flex flex-wrap items-center py-5">
              <div className="lg:w-1/2 w-full">
                <img
                  src={image1}
                  className="w-85 m-auto"
                  alt="image1"
                  loading="lazy"
                />
              </div>
              <div 
                id="section1" 
                ref={sectionRef1} 
                className={`lg:w-1/2 w-full font-poppins lg:text-left text-center pt-4 pb-4 lg:pr-10 px-5 border-gray-300 border rounded-xl shadow-lg hover:shadow-xl transition duration-150 ease-in-out pre-animation-right ${inView1 ? 'slide-in-right' : ''}`} 
                style={{backgroundColor: "white" }}
              >
                <h3 className="text-2xl tracking-wide pb-3">
                  Select your budget, preferred locations and yield expectations
                </h3>
                <p className="text-base leading-7 tracking-wide pb-3">
                  Using innovative, property technology and data-analysis tools
                  we will instantly source a fully vetted list of profitable
                  investment opportunities that match your search criteria.
                  These will be packaged up into an easy-to-understand document
                  ready for you to approve.
                </p>
                <p className="text-sm leading-7 tracking-wide">
                  Rest assured, all properties go through a full, comprehensive
                  due diligence process. You'll receive a full financial
                  break-down, comparable properties in the market, information
                  about the local area, refurbishment requirements (if any) and
                  estate agent valuations for sales and lettings.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl m-auto py-16 px-4 md:px-8">
          <div className="flex flex-wrap items-center py-5">
            <div 
              ref={sectionRef2} 
              className={`lg:w-1/2 w-full font-poppins lg:text-left text-center pt-4 pb-4 lg:pr-10 px-5 border-gray-300 border rounded-xl shadow-lg hover:shadow-xl transition duration-150 ease-in-out pre-animation-left ${inView2 ? 'slide-in-left' : ''}`} 
              style={{backgroundColor: "white" }}
            >
              <h3 className="text-2xl tracking-wide pb-3">
                Watch your residential property portfolio, and wealth, grow!
              </h3>
              <p className="text-base leading-7 tracking-wide pb-3">
                There is no limit to the number of properties you can hold
                within your portfolio. You'll always own the assets, fully
                benefit from the capital appreciation, as well as the cashflow
                from renting.
              </p>
              <p className="text-sm leading-7 tracking-wide">
                Ready to start your property investment journey? Get started
                today to tell us your search criteria and we'll share a fully
                bespoke, curated list of fully vetted properties that match
                your budget, location and profitability requirements.
              </p>
            </div>
            <div className="lg:w-1/2 w-full">
              <img
                src={image3}
                className="w-85 m-auto"
                alt="Cityscape"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <div className="relative w-full" style={{ 
          backgroundImage: `url(${backgroundPattern})`, 
          backgroundRepeat: 'repeat', 
          backgroundSize: '75px 75px' 
        }}>
          <div className="max-w-7xl m-auto py-16 px-4 md:px-8">
            <div className="flex flex-wrap items-center">
              <div className="lg:w-1/2 w-full">
                <img
                  src={image2}
                  className="w-85 m-auto"
                  alt="Stamp Duty"
                  loading="lazy"
                />
              </div>
              <div 
                ref={sectionRef3} 
                className={`lg:w-1/2 w-full font-poppins lg:text-left text-center pt-4 pb-4 lg:pr-10 px-5 border-gray-300 border rounded-xl shadow-lg pre-animation-right ${inView3 ? 'slide-in-right' : ''}`} 
                style={{backgroundColor: "white" }}
              >
                <h3 className="text-2xl tracking-wide pb-3">
                  The entire process is taken care of for you - from sourcing to
                  letting
                </h3>
                <p className="text-base leading-7 tracking-wide pb-3">
                  The entire deal will be managed by a team of experts. Finding
                  the properties and carrying out full due diligence, managing
                  the buying process to get you the best deal, pricing and
                  managing the refurbishment, sourcing tenants, letting
                  management, even your bookkeeping and accounts.
                </p>
                <p className="text-sm leading-7 tracking-wide">
                  You'll be kept up to date throughout the entire process – as
                  little or as much as you want to be. Building a residential
                  property portfolio is not only easy, but enjoyable. We'll help
                  you enjoy a hassle-free investment journey which allows you to
                  divert your attention elsewhere.
                </p>
              </div>
            </div>
          </div>

          {/* Learn More Button */}
          <div className="justify-center flex pt-14">
            <button
              onClick={handleLearnMore}
              ref={buttonRef1}
              className={`${buttonInView1 ? 'pulse' : ''} w-auto sm:w-auto py-2 px-6 rounded-md cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105`}
              style={{
                background: themes.color2,
                color: isWhiteOrShade(themes.color2) ? "#000000" : "#ffffff",
                border: isWhiteOrShade(themes.color2) ? "1px solid #000000" : "none",
              }}
              onMouseOver={(e) => handleHover(e, themes.color1)}
              onMouseOut={(e) => handleHover(e, themes.color2)}
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* <<============4 colr============>> */}
      <div className="w-full m-auto bg-white-f2f9fa">
        <div className="max-w-7xl m-auto lg:py-24 py-14 text-primarycolor font-poppins lg:px-0 px-5">
          <h3 className="lg:text-4xl text-2xl font-semibold text-center leading-tight pb-6">
            Buy-to-let investments -<br></br>a reliable way to build wealth while
            you sleep
          </h3>
          <h5 className="lg:text-2xl text-lg font-medium tracking-wide text-center">
            Buy-to-let investment properties are among the most reliable and
            profitable investments.
          </h5>
          <img
            src={image4}
            className="h-50 m-auto"
            alt="Cityscape"
            loading="lazy"
          />
          <div className="pt-14">
            <div className="grid lg:grid-cols-4 grid-cols-1 gap-5 m-auto">
              {[
                {
                  title: "Invest In A Stable Market",
                  description: "48% of rental properties are owned by portfolio landlords (10 or more properties)"
                },
                {
                  title: "Benefit From Predictability",
                  description: "Investing in residential property is less volatile than other markets"
                },
                {
                  title: "See Double Profitability",
                  description: "Receive rent monthly, but also an increase in property value over-time"
                },
                {
                  title: "Little Effort Required",
                  description: "We take care of everything so you can divert your attention elsewhere"
                }
              ].map((item, index) => (
                <div key={index} className="flex flex-col justify-center p-6 rounded-xl border-gray-300 border shadow-xl transition duration-150 ease-in-out hover:shadow-2xl" style={{backgroundColor: "white"}}>
                  <h3 className="font-roboto text-xl font-medium text-center mb-4">
                    {item.title}
                  </h3>
                  <p className="font-roboto text-base font-normal tracking-wide text-center">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
            {/* Get Started Button */}
            <div className="justify-center flex pt-14">
              <button
                onClick={handleGetStarted}
                ref={buttonRef2}
                className={`${buttonInView2 ? 'pulse' : ''} w-auto sm:w-auto py-2 px-6 rounded-md cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105`}
                style={{
                  background: themes.color2,
                  color: isWhiteOrShade(themes.color2) ? "#000000" : "#ffffff",
                  border: isWhiteOrShade(themes.color2) ? "1px solid #000000" : "none",
                }}
                onMouseOver={(e) => handleHover(e, themes.color1)}
                onMouseOut={(e) => handleHover(e, themes.color2)}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full m-auto bg-white-f2f9fa">
        <div className="relative w-full" style={{ 
          backgroundImage: `url(${backgroundPattern})`, 
          backgroundRepeat: 'repeat', 
          backgroundSize: '75px 75px' 
        }}>
          <div className="max-w-7xl m-auto lg:py-24 py-14 text-primarycolor font-poppins px-5">
            <h3 className="lg:text-4xl text-2xl font-semibold text-center leading-tight pb-6">
              What do our portfolio landlords think?
            </h3>
            <div className="pt-6">
              <div className="grid lg:grid-cols-3 grid-cols-1 gap-5 m-auto">
                {[
                  {
                    text: "This site takes the stress out of finding properties for myself. The team is easy to talk to and always quick to respond. I have purchased multiple properties and will continue to do so in the future.",
                    author: "Mr R"
                  },
                  {
                    text: "This business is like a family who welcome you into the fold. They only work with trusted parties so you're coming into a group with a wealth of knowledge and trust. One of the best decisions I've ever made.",
                    author: "Mrs M"
                  },
                  {
                    text: "I sourced my first deal as a property newbie and the service I received was exceptional. I was led through the entire process step-by-step and the communication was always thorough and consistent. Highly recommend.",
                    author: "Mr A. K"
                  }
                ].map((review, index) => (
                  <div
                    key={index}
                    ref={index === 0 || index === 2 ? sectionRef4 : null}
                    className={`flex flex-col justify-center p-6 rounded-xl border-gray-300 border shadow-xl transition duration-150 ease-in-out hover:shadow-2xl ${
                      (index === 0 && inView4) ? 'slide-in-left' : 
                      (index === 2 && inView4) ? 'slide-in-right' : ''
                    } ${
                      (index === 0) ? 'pre-animation-left' : 
                      (index === 2) ? 'pre-animation-right' : ''
                    }`}
                    style={{ backgroundColor: "white" }}
                  >
                    <h3 className="font-poppins text-sm font-normal leading-6 mb-4 text-center">
                      {review.text}
                    </h3>
                    <ReviewStars />
                    <p className="font-roboto text-base font-normal tracking-wide text-center mt-4">
                      {review.author}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full m-auto bg-[#F8F8F9]">
        <div className="max-w-7xl m-auto py-24 text-primarycolor font-poppins lg:px-0 px-5">
          <h3 className="lg:text-4xl text-2xl font-semibold text-center leading-tight pb-6">
            Ready to get started?
          </h3>
          <h5 className="text-xl font-normal tracking-wide text-center">
            Join our investor community today and starting browsing our
            high-yielding investment opportunities
          </h5>
          <div className="flex justify-center pt-5">
            <button
              onClick={handleSignUp}
              ref={buttonRef3}
              className={`${buttonInView3 ? 'pulse' : ''} w-auto sm:w-auto py-2 px-6 rounded-md cursor-pointer transition-transform duration-300 ease-in-out transform hover:scale-105`}
              style={{
                background: themes.color2,
                color: isWhiteOrShade(themes.color2) ? "#000000" : "#ffffff",
                border: isWhiteOrShade(themes.color2) ? "1px solid #000000" : "none",
              }}
              onMouseOver={(e) => handleHover(e, themes.color1)}
              onMouseOut={(e) => handleHover(e, themes.color2)}
            >
              Sign Up Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WhiteLabel;